import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { ClientSelector } from 'src/components/MergeClientModal/client-selector';
import { Client, useMergeClient } from 'src/graphql';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';

export const MergeClientModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeClientModal);
  const { isOpened, modalProps } = useModalState(Modals.MergeClientModal);
  const [clientTarget, setClientTarget] = useState<Client | null>(null);
  const { mergeClient } = useMergeClient();
  const client = modalProps?.client;

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const closeMergeModal = useCallback(() => {
    closeModal();
    setClientTarget(null);
  }, [closeModal]);

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { customViewId } = useRouteParams(UrlParams.customViewId);

  const { customViews } = useCustomViews({
    entity: EntityNames.Client,
    partnerId: selectedPartnerId,
  });

  const customView = useMemo(
    () => customViews.find((i) => i.customViewId === customViewId),
    [customViews, customViewId],
  );

  const { navigate } = useNavigateRoute();

  const handleMergeClients = useCallback(async () => {
    if (!clientTarget?.clientId) {
      showErrorAlert(`Invalid client target ${clientTarget?.clientId}`);

      return;
    }

    if (!client?.clientId) {
      showErrorAlert(`Invalid client origin ${client?.clientId}`);

      return;
    }

    closeMergeModal();

    const response = await mergeClient({
      clientTarget,
      clientOrigin: client,
    });

    if (!response) {
      return;
    }

    if (response.error) {
      console.error(response.error);
      showErrorAlert(response.error);
      return;
    }

    showSuccessAlert('Clients were merged');
    setClientTarget(null);

    if (customView?.entityName === EntityNames.Client) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customViewId,
          clientTarget?.clientId,
        ),
      );
    }
  }, [
    client,
    clientTarget,
    closeMergeModal,
    mergeClient,
    showSuccessAlert,
    customView?.entityName,
    showErrorAlert,
    navigate,
    customViewId,
  ]);

  const filters = useMemo(() => (client ? [client] : []), [client]);

  return (
    <Dialog open={isOpened} onClose={closeMergeModal} fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2}>
          <Typography>Merge Clients</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <ClientSelector client={client} deletable={false} title="Origin Client" />
          <ClientSelector
            deletable
            title="Target Client"
            filters={filters}
            partnerId={client?.partnerId ?? selectedPartnerId}
            client={clientTarget}
            setClient={setClientTarget}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleMergeClients} disabled={!client || !clientTarget}>
          Merge clients
        </Button>
      </DialogActions>
    </Dialog>
  );
};
